import "../styles/chatbot.css";
import ChatBotContainer from "../components/ChatBotContainer";
import { Sun, Moon, LogOut, Bot } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
// import CHAT_MODES from "../constants/modes";
// import { chatModesMap } from "../config/chatModesMap";
import { logout } from "../store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { APP_ROUTES } from "../constants";
import { useAppSelector } from "../hooks/redux";
import instanceService from "../services/instanceService";
import { LoadingSpinner } from "../components/common/ui/LoadingSpinner";

function ThemeToggle({ isDark, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`p-1.5 rounded-full transition-colors ${
        isDark
          ? "bg-gray-800 text-yellow-400 hover:bg-gray-700"
          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
      }`}
      aria-label="Toggle theme"
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? (
        <Sun size={20} />
      ) : (
        <Moon size={20} className="text-gray-500" />
      )}
    </button>
  );
}

function Header({ isDark, onThemeToggle }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate(APP_ROUTES.LOGIN);
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm fixed top-0 left-0 right-0 h-14 z-50 transition-colors">
      <div className="flex items-center h-full px-4 md:px-6 max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold text-gray-800 dark:text-white">
            Welcome to the chatbot
          </span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-1">
            <ThemeToggle isDark={isDark} onToggle={onThemeToggle} />
            <button
              onClick={handleLogout}
              className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              role="menuitem"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function Chat() {
  const [, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatModesMap, setChatModesMap] = useState({});
  const { user } = useAppSelector((state) => state.auth);
  const [activeMode, setActiveMode] = useState("");
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("theme") === "dark" ||
        (!localStorage.getItem("theme") &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      );
    }
    return false;
  });

  useEffect(() => {
    if (isDarkTheme) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkTheme]);

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    const company_id = user.company_id;
    const user_instance_id = user.instance_id;
    try {
      if (company_id && user_instance_id) {
        const data = await instanceService.getAllByCompany(
          parseInt(company_id)
        );
        if (Array.isArray(data) && data?.length > 0) {
          const userInstance = data.filter(
            (instance) => instance.instance_id === user_instance_id
          );
          setInstances([...userInstance]);
          if (userInstance.length > 0) {
            const primaryMode = userInstance[0]?.instance_name;
            setActiveMode(primaryMode.toUpperCase());
            buildChatModesMap(userInstance);
          } else {
            console.warn(
              "No instances found for user's instance_id:",
              user_instance_id
            );
            setInstances([]);
            setActiveMode("");
            setChatModesMap({});
          }
        }
      }
    } catch (error) {
      console.error("Error loading instances:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, user?.instance_id]);
  const buildChatModesMap = (data) => {
    const modes = {};

    data.forEach((mode) => {
      const key = mode.instance_name.toUpperCase();
      modes[key] = {
        icon: <Bot size={20} />,
        label: mode.instance_name,
        instance_id: mode.instance_id,
        application_id: mode.application_id,
      };
    });
    setChatModesMap(modes);
  };
  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  return (
    <div className="w-full h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 transition-colors">
      <Header isDark={isDarkTheme} onThemeToggle={toggleTheme} />
      {loading ? (
        <LoadingSpinner className="h-64" />
      ) : (
        <div className="flex h-[calc(100vh-3.5rem)] mt-14">
          <aside className="hidden md:flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 w-60 fixed h-[calc(100vh-3.5rem)] transition-colors">
            <div className="p-3">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                Chat Modes
              </h2>

              <nav className="space-y-1">
                {Object.entries(chatModesMap).map(
                  ([modeKey, { icon, label }]) => {
                    const isActive = activeMode === modeKey;
                    return (
                      <button
                        key={modeKey}
                        onClick={() => setActiveMode(modeKey)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors
          ${
            isActive
              ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/50 font-medium"
              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
                      >
                        {icon}
                        <span>{label}</span>
                      </button>
                    );
                  }
                )}
              </nav>
            </div>

            {/* <div className="flex-1 p-3 border-t border-gray-200 dark:border-gray-700 overflow-y-auto">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                Previous Chats
              </h2>
              <nav className="space-y-1">
                <button className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors">
                  <MessageSquare size={16} />
                  <span>Last Week Sales Summary </span>
                </button>
                <button className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors">
                  <MessageSquare size={16} />
                  <span>Customers Summary</span>
                </button>
              </nav>
            </div> */}
          </aside>

          <main className="flex-1 md:ml-60 bg-white h-[calc(100vh-3.5rem)] transition-colors">
            {activeMode && Object.keys(chatModesMap).length > 0 && (
              <ChatBotContainer
                initialMode={activeMode}
                chatModesMap={chatModesMap}
              />
            )}
          </main>
        </div>
      )}
    </div>
  );
}

export default Chat;
